import { inject, Injectable } from '@angular/core';
import { TableService } from '@docpath/linces-angular-components/common';
import { HeaderAction, SearchMode, SearchOptionType, SearchRowInfo, SearchRowInfoSelect } from '@docpath/webcomponents';
import { faMagnifyingGlass, faPlusCircle } from '@fortawesome/free-solid-svg-icons';

import { Priority } from '../../../configuration/alert-rules/models/alert-rule';
import { AlertResource, AlertStatus } from '../../models/alert';
import { MonitoringConstants } from '../../monitoring-constants';
import { MonitoringFormService } from '../../services/monitoring-form.service';
import { MonitoringHttpService } from '../../services/monitoring-http.service';

@Injectable({
    providedIn: 'root'
})
export class MonitoringHeaderService {
    private readonly i18nPrefix = 'monitoring';

    private readonly tableService = inject(TableService);
    private readonly monitoringHttpService = inject(MonitoringHttpService);
    private readonly monitoringFormService = inject(MonitoringFormService);

    getRightActions(): HeaderAction[] {
        return [this.initSearchAction()];
    }

    updateRightActions(dgeId: number, rightActions: HeaderAction[]): HeaderAction[] {
        const assignLabel = `${this.i18nPrefix}.header.assign-alert-rule.label`;
        const filteredActions = rightActions.filter(action => 
            
             (action as { title?: string }).title !== assignLabel
        );
        filteredActions.unshift(this.initAssignAlertRuleAction(dgeId));

        return filteredActions;
    }

    getSearchAction(actions: HeaderAction[]): HeaderAction {
        return actions.length === 1 ? actions[0] : actions[1];
    }

    initSearchAvailableOptions(): SearchRowInfo[] {
        const columnsConfig = this.tableService.getColumns(this.i18nPrefix);

        const filters = [
            new SearchRowInfo(true, MonitoringConstants.NAME, undefined, SearchOptionType.TEXT, SearchMode.exactly, true, true),
            new SearchRowInfoSelect(false, MonitoringConstants.RESOURCE, undefined, SearchMode.is, this.getResourceFilterOptions(), true),
            new SearchRowInfoSelect(false, MonitoringConstants.PRIORITY, undefined, SearchMode.is, this.getPriorityFilterOptions(), true),
            new SearchRowInfoSelect(false, MonitoringConstants.STATUS, undefined, SearchMode.is, this.getStatusFilterOptions(), true),
            new SearchRowInfo(false, MonitoringConstants.DATE, undefined, SearchOptionType.DATE_RANGE, SearchMode.from, undefined, true, undefined, true)
        ];

        filters.forEach(filter => {
            const columnConfig = columnsConfig.find(column => column.name === filter.key);

            if (columnConfig) {
                filter.visible = columnConfig.visible;
            }
        });

        return filters;
    }

    private getPriorityFilterOptions(): Map<string, string> {
        return new Map<string, string>([
            [`${this.i18nPrefix}.table.priority.option.critical`, Priority.Critical],
            [`${this.i18nPrefix}.table.priority.option.low`, Priority.Low],
            [`${this.i18nPrefix}.table.priority.option.medium`, Priority.Medium]
        ]);
    }

    private getResourceFilterOptions(): Map<string, string> {
        return new Map<string, string>([
            [`${this.i18nPrefix}.table.resource.option.cpu`, AlertResource.Cpu],
            [`${this.i18nPrefix}.table.resource.option.disk`, AlertResource.Disk],
            [`${this.i18nPrefix}.table.resource.option.java`, AlertResource.Java],
            [`${this.i18nPrefix}.table.resource.option.ram`, AlertResource.Ram]
        ]);
    }

    private getStatusFilterOptions(): Map<string, string> {
        return new Map<string, string>([
            [`${this.i18nPrefix}.table.status.option.fulfilled`, AlertStatus.Fulfilled],
            [`${this.i18nPrefix}.table.status.option.not-fulfilled`, AlertStatus.NotFulfilled]
        ]);
    }

    private initSearchAction(): HeaderAction {
        const action = new HeaderAction(`${this.i18nPrefix}.header.search.label`, { cssClass: 'btn btn-dpsecondary' });
        action.tooltip = `${this.i18nPrefix}.header.search.tooltip`;
        action.icon = faMagnifyingGlass;

        return action;
    }

    private initAssignAlertRuleAction(dgeId: number): HeaderAction {

        const action = new HeaderAction(`${this.i18nPrefix}.header.assign-alert-rule.label`);

        action.icon = faPlusCircle;
        action.tooltip = `${this.i18nPrefix}.header.assign-alert-rule.tooltip`;

        action.action = () => {
            this.monitoringHttpService.getAssignedAlertRules(dgeId).subscribe(assignedAlertRules => {
                this.monitoringFormService.openAssignAlertRuleForm(dgeId, assignedAlertRules);
            });
        };

        return action;
    }
}
